<template>
    <div class="filters-container">
        <!-- Header compacto -->
        <!-- Header compacto -->
        <div class="filters-header">
            <div class="d-flex align-items-center">
                <span class="text-muted font-weight-bold" style="font-size: 13px;">
                    <i class="fa fa-list-ul mr-1"></i> Filtros aplicados
                </span>
            </div>

            <div class="d-flex align-items-center">
                <b-button v-if="localCompanyId" variant="link" class="text-danger p-0 text-decoration-none mr-3"
                    style="font-size: 13px;" @click="handleClearFilters">
                    <i class="fa fa-times mr-1"></i> Limpiar filtros
                </b-button>

                <button class="toggle-btn" @click="showAdvancedFilters = !showAdvancedFilters"
                    :disabled="!localCompanyId">
                    <i :class="showAdvancedFilters ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                    {{ showAdvancedFilters ? "Menos filtros" : "Más filtros" }}
                </button>
            </div>
        </div>

        <!-- Filtros básicos (siempre visibles) -->
        <div class="filters-grid">
            <!-- Col 1: Empresa (Siempre habilitado para iniciar) -->
            <div>
                <el-select id="empresa" name="empresa" filterable clearable v-model="localCompanyId"
                    placeholder="Seleccione la empresa" size="small" remote :remote-method="searchCompany"
                    :loading="loading" class="w-100">
                    <el-option v-for="empresa in empresas" :key="empresa.id" :label="empresa.nombre"
                        :value="empresa.id" />
                </el-select>
            </div>

            <!-- Col 2: Ubicación -->
            <div>
                <tree-select v-model="filter.location_id" id="ubicacion" name="ubicacion" :multiple="false"
                    :defaultExpandLevel="Infinity" :options="treeWithChildrens" :disabled="!localCompanyId || loading"
                    placeholder="Todas las ubicaciones" class="custom-tree-select" />
            </div>

            <!-- Col 3: Estado -->
            <div>
                <el-select v-model="filter.status" multiple clearable placeholder="Todos los estados" size="small"
                    class="w-100" :disabled="!localCompanyId || loading">
                    <el-option 
                        v-for="status in statusOptions" 
                        :key="status.value" 
                        :label="status.label" 
                        :value="status.value" 
                    />
                </el-select>
            </div>
        </div>

        <!-- Filtros avanzados (colapsables) -->
        <transition name="expand">
            <div v-if="localCompanyId && showAdvancedFilters" class="filters-grid advanced">
                <!-- Vencimiento -->
                <div>
                    <el-date-picker v-model="expirationDateRange" type="daterange" range-separator="a"
                        start-placeholder="Inicio" end-placeholder="Fin" format="dd/MM/yyyy" value-format="yyyy-MM-dd"
                        size="small" class="w-100" :disabled="!localCompanyId || loading">
                    </el-date-picker>
                </div>

                <!-- Responsable -->
                <div>
                    <el-select v-model="filter.responsible_id" filterable clearable placeholder="Todos los responsables"
                        size="small" class="w-100" :disabled="!localCompanyId || loading">
                        <el-option v-for="worker in safeWorkers" :key="worker.id" :label="worker.fullName"
                            :value="worker.id" />
                    </el-select>
                </div>

                <!-- Prioridad -->
                <div>
                    <el-select v-model="filter.priority" multiple clearable placeholder="Todas las prioridades" size="small"
                        class="w-100" :disabled="!localCompanyId || loading">
                        <el-option 
                            v-for="priority in priorityOptions" 
                            :key="priority.value" 
                            :label="priority.label" 
                            :value="priority.value" 
                        />
                    </el-select>
                </div>

                <!-- Origen -->
                <div>
                    <el-select v-model="filter.module_origin" multiple clearable placeholder="Todos los orígenes" size="small"
                        class="w-100" :disabled="!localCompanyId || loading">
                        <el-option 
                            v-for="origin in originOptions" 
                            :key="origin.value" 
                            :label="origin.label" 
                            :value="origin.value" 
                        />
                    </el-select>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import TreeSelect from "@riophae/vue-treeselect";
import { mapActions, mapState } from "vuex";

export default {
    name: "FiltersTracings",
    components: { TreeSelect },
    data() {
        return {
            localCompanyId: null,
            showAdvancedFilters: false,
            filter: {
                location_id: null,
                responsible_id: null,
                status: ['Abierto', 'Proceso'],
                priority: [],
                module_origin: [],
            },
            expirationDateRange: null,
        };
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        selectedCompanyId: {
            type: [Number, String],
            default: null,
        },
        statusOptions: {
            type: Array,
            default: () => [],
        },
        priorityOptions: {
            type: Array,
            default: () => [],
        },
        originOptions: {
            type: Array,
            default: () => [],
        },
    },
    methods: {
        ...mapActions("location", ["getTreeWithChildrens"]),
        ...mapActions("trainings", ["fetchWorkers"]),
        ...mapActions("tareas", ["obtenerEmpresas"]),

        async searchCompany(val) {
            await this.obtenerEmpresas(val);
        },

        async loadFiltersData() {
            if (!this.localCompanyId) return;

            try {
                this.$emit('loading-start');
                await Promise.all([
                    this.getTreeWithChildrens(this.localCompanyId),
                    this.fetchWorkers(this.localCompanyId),
                ]);
            } catch (error) {
                console.error("Error loading filters data", error);
            } finally {
                this.$emit('loading-end');
            }
        },

        emitFilters() {
            const filters = {};

            // Only include non-empty filters
            if (this.filter.location_id) {
                filters.location_id = [this.filter.location_id];
            }
            if (this.filter.responsible_id) {
                filters.responsible_id = [this.filter.responsible_id];
            }
            if (this.filter.status && this.filter.status.length > 0) {
                filters.status = this.filter.status;
            }
            if (this.filter.priority && this.filter.priority.length > 0) {
                filters.priority = this.filter.priority;
            }
            if (this.filter.module_origin && this.filter.module_origin.length > 0) {
                filters.module_origin = this.filter.module_origin;
            }
            if (this.expirationDateRange && this.expirationDateRange.length === 2) {
                filters.date_start = this.expirationDateRange[0];
                filters.date_end = this.expirationDateRange[1];
            }

            this.$emit("filters-changed", filters);
        },

        handleClearFilters() {
            this.filter = {
                location_id: null,
                responsible_id: null,
                status: ['Abierto', 'Proceso'],
                priority: [],
                module_origin: [],
            };
            this.expirationDateRange = null;
            this.$emit('clear-filters');
        },
    },
    computed: {
        ...mapState("location", ["treeWithChildrens"]),
        ...mapState("trainings", ["workers"]),
        ...mapState("tareas", ["empresas"]),

        safeWorkers() {
            if (!this.workers || !this.workers.length) return [];
            return this.workers.map((worker) => ({
                ...worker,
                fullName: `${worker.names || ''} ${worker.surnames || ''}`.trim(),
            }));
        },
    },
    watch: {
        selectedCompanyId: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal !== this.localCompanyId) {
                    this.localCompanyId = newVal;
                }
            }
        },
        localCompanyId: {
            async handler(newVal) {
                if (newVal) {
                    await this.loadFiltersData();
                    this.emitFilters(); // Emit filters with default values
                    this.$emit("company-changed", newVal);
                } else {
                    this.handleClearFilters();
                    this.$emit("company-changed", null);
                }
            },
        },
        filter: {
            handler() {
                this.emitFilters();
            },
            deep: true,
        },
        expirationDateRange() {
            this.emitFilters();
        },
    },
};
</script>

<style scoped>
.filters-container {
    background: transparent;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px;
}

/* Header compacto */
.filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.toggle-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 12px;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
}

.toggle-btn:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
}

.toggle-btn i {
    font-size: 10px;
}

/* Grid de filtros */
.filters-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.filters-grid.advanced {
    margin-top: 8px;
}

/* Animación de expansión */
.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease;
    max-height: 200px;
    overflow: hidden;
}

.expand-enter,
.expand-leave-to {
    max-height: 0;
    opacity: 0;
}

/* Overrides para Third Party Components para que coincidan con size="small" */
::v-deep .vue-treeselect__control {
    border-radius: 4px;
    border-color: #DCDFE6;
    height: 32px !important;
    /* Aproximar al size="small" de Element UI */
    line-height: 30px !important;
    font-size: 13px;
}

::v-deep .vue-treeselect__placeholder,
::v-deep .vue-treeselect__single-value {
    line-height: 30px !important;
}

/* Responsive */
@media (max-width: 992px) {
    .filters-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 576px) {
    .filters-grid {
        grid-template-columns: 1fr;
    }

    .filters-header {
        flex-direction: column;
        gap: 8px;
        align-items: stretch;
    }

    .toggle-btn {
        justify-content: center;
    }
}
</style>
